import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  usePropertyField,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { isWashingtonState } from '../utils/washington';

const pdfLinkDef: SlotGroupDefinition = {
  label: 'Qualifying Criteria PDF',
  fields: {
    url: {
      label: 'PDF URL',
      type: 'url',
      defaultValue:
        'https://media.multihub.io/property/6767906449167306/01a18923-213c-720e-83d4-648ce0dc5dbc.pdf',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Full Qualifying Criteria (PDF)',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

/**
 * QualifyingCriteria — a single centered richtext blurb of the community's
 * qualifying rental criteria, followed by a link to the full criteria PDF.
 * The whole block is editable per-property from the inline editor; clearing
 * the PDF URL hides the button.
 *
 * Washington-only: non-WA properties hide this page (and the header CTAs)
 * and redirect deep links home.
 */
export function QualifyingCriteria() {
  const state = usePropertyField('property_address_state');
  const isWA = isWashingtonState(state);
  const pdfLink = useSlotGroup('qualifyingcriteria.pdf_link', pdfLinkDef);
  const pdfUrl = pdfLink.url?.trim();

  useEffect(() => {
    if (isWA) return;
    if (typeof window === 'undefined') return;
    window.location.replace('/');
  }, [isWA]);

  if (!isWA) return null;

  return (
    <section className="pt-10 pb-16 md:pt-14 md:pb-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl md:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="qualifyingcriteria.blurb"
          label="Page Blurb"
          as="div"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0"
        />

        <div className="mt-8">
          <EditableSlotGroup groupPath="qualifyingcriteria.pdf_link" definition={pdfLinkDef}>
            {pdfUrl ? (
              <a
                href={pdfUrl}
                target={pdfLink._target || '_blank'}
                rel="noopener noreferrer"
                className="qc-pdf-cta"
              >
                {pdfLink.label || 'View Full Qualifying Criteria (PDF)'}
              </a>
            ) : null}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .qc-pdf-cta {
          display: inline-block;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-text-on-secondary);
          background: var(--vibe-color-secondary);
          border: 2px solid var(--vibe-color-secondary);
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .qc-pdf-cta:hover {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: var(--vibe-color-secondary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'QualifyingCriteria',
  displayName: 'Qualifying Criteria',
  tokens: {
    'qualifyingcriteria.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue:
        '<p>Qualifying Rental Criteria</p><p>Touring: Government issued I.D is required to tour.</p><p>Income: Income verification is required during the application process.</p><p>Credit/Criminal: Unsatisfactory credit may require additional deposit &amp; criminal background checks are required.</p>',
    },
  },
  slotGroups: {
    'qualifyingcriteria.pdf_link': pdfLinkDef,
  },
};

(QualifyingCriteria as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default QualifyingCriteria;
